/**
 * Auth redesign tokens — MagicBI auth screens spec (design package
 * `magicbi-auth/README.md` §3, prototype `index.html` :root).
 *
 * The purple scale, pill radius and base type stack already exist app-wide and
 * are referenced from `design-system/tokens`. Only values with no app-wide token
 * live here. Kept separate from `authTokens.ts` (the older blue auth tokens).
 */
import { rawColors } from "../design-system/tokens/colors";
import { borderRadius } from "../design-system/tokens/borders";
import {
  fontFamilies,
  fontSizes,
  fontWeights,
} from "../design-system/tokens/typography";

// ── Colour ──────────────────────────────────────────────────────────────────

/** Spec `--ink-*`: the neutral scale (purple-tinted greys). No app-wide equivalent. */
export const authInk = {
  900: rawColors.brand.primaryDarker, // #18102B — text, dark ground
  800: "#241C35",
  700: "#3F3850", // body text in the card
  600: "#5C5468", // subtitles, secondary text
  500: "#7A7287", // muted, captions, legal
  400: "#A39CB0", // placeholder, inactive carousel tab
  300: "#CFCAD9", // hover border, footer links on dark
  200: "#E6E1F3", // input / outline borders, dividers
  100: "#F3F1F7", // card section rule, Member badge
  50: "#F7F6FA", // email chip, search field, option hover
} as const;

export const authBrandColors = {
  white: rawColors.structural.white,
  /** Spec `--lilac` #AA97D8 — the 1-step difference from the brand token isn't visible. */
  lilac: rawColors.brand.primaryLight,
  mist: "#EDF3FC",
  blush: "#F4EAF3",
  /** Ink paired with the blush avatar tint. */
  blushInk: "#9A3D7A",
  success: "#1F9D6B",
  successInk: "#17714D",
  successFill: "#E3F5EC",
  error: "#D2414B",
  errorFill: "#FCE8E9",
  info: "#2F6FD6",
  infoFill: "#EDF3FC",
} as const;

/** Page background: three brand glows over ink-900. */
export const authGround = [
  `radial-gradient(55% 60% at 0% 0%, ${rawColors.primary[500]} 0%, transparent 62%)`,
  `radial-gradient(50% 60% at 100% 100%, ${rawColors.primary[700]} 0%, transparent 70%)`,
  // primary[400] (#8368C2) at 35%
  "radial-gradient(40% 40% at 70% 20%, rgba(131, 104, 194, 0.35) 0%, transparent 70%)",
  authInk[900],
].join(", ");

export const authGrain = {
  opacity: 0.4,
  blendMode: "overlay",
} as const;

// ── Assets (served from /public) ────────────────────────────────────────────

export const authAssets = {
  /** Spec grain.png re-encoded as WebP (110KB → 39KB), tiled over the ground. */
  grain: "/auth/grain.webp",
  /**
   * The spec's own magicbi-horizontal-dark.png. The app's
   * /logo-with-text-white.png is the same artwork without the 10px padding,
   * so at 34px tall it drew the wordmark 24px high instead of the references' 21.
   */
  logoOnDark: "/auth/magicbi-horizontal-dark.png",
  /** Same artwork as the spec's magicbi-mark — transparent, so it also works off-white. */
  mark: "/logos/magicbi.svg",
  /**
   * The spec's own magicbi-mark.png for the top of the card. Its padding
   * differs from the SVG's, and the reference screens are drawn with it
   * (glyph 39×38 inside a 44px-wide box). White background: card use only.
   */
  cardMark: "/auth/magicbi-mark.png",
  /** Spec sparkle-purple.svg: the accent on the B1 inbox icon circle. */
  sparkle: "/auth/sparkle-purple.svg",
} as const;

// ── Elevation & rings ───────────────────────────────────────────────────────

export const authShadows = {
  card: "0 2.5rem 5rem -1.25rem rgba(0, 0, 0, 0.55)",
  menu: "0 1.25rem 2.5rem -0.5rem rgba(24, 16, 43, 0.22)",
  float: "0 1.5rem 3rem -0.75rem rgba(0, 0, 0, 0.45)",
  glow: "0 0 0 0.375rem rgba(170, 151, 216, 0.25), 0 0 3rem rgba(170, 151, 216, 0.5)",
  focusRing: "0 0 0 0.25rem rgba(100, 66, 179, 0.16)",
  errorRing: "0 0 0 0.25rem rgba(210, 65, 75, 0.12)",
} as const;

/** Keyboard focus outline for links and text buttons (spec `:focus-visible`, 2px / 2px). */
export const authFocusOutline = {
  width: "0.125rem",
  offset: "0.125rem",
} as const;

// ── Shape ───────────────────────────────────────────────────────────────────

export const authRadius = {
  /** Spec 28 — no step on the app scale (24 → full). */
  card: "1.75rem",
  /** Spec 20 — card radius under 520px. */
  cardCompact: "1.25rem",
  /** Spec 14 — carousel artifact cards. */
  tile: "0.875rem",
  input: borderRadius.xl, // 12
  option: borderRadius.xl, // 12
  menu: borderRadius["2xl"], // 16
  iconTile: borderRadius["2xl"], // 16
  badge: borderRadius.md, // 6
  pill: borderRadius.full,
  /** Spec 10 — the 36px option avatar (`.mb-avatar`). */
  avatar: "0.625rem",
  /** Spec 9 — the 30px avatar in the combobox trigger (`.mb-avatar--sm`). */
  avatarCompact: "0.5625rem",
  /** Spec 10 — the search field at the top of the combobox menu. */
  searchField: "0.625rem",
} as const;

/**
 * Spec control border. CLAUDE.md exempts only 1px hairlines from rem; 1.5px is
 * held here as the one named exception so it can't drift (flagged in handoff).
 */
export const authBorderWidth = "1.5px";

// ── Spacing ─────────────────────────────────────────────────────────────────

/** Spec spacings with no step on the 4px scale (14px sits between 3 and 4). */
export const authSpacing = {
  /** Gap between a form's field and its submit button (`.mb-form`). */
  formGap: "0.875rem",
  /** Side padding of the email chip and banners. */
  insetX: "0.875rem",
  /** Side padding of the 20px role badge (`.mb-role`); 7px is off the 4px scale. */
  badgeX: "0.4375rem",
} as const;

// ── Size & layout ───────────────────────────────────────────────────────────

export const authSizes = {
  cardMaxWidth: "27.5rem", // 440
  controlHeight: "3rem", // 48 — inputs, primary/accent buttons
  socialButtonHeight: "2.875rem", // 46
  comboboxMinHeight: "3.25rem", // 52
  searchFieldHeight: "2.5rem", // 40
  menuMaxHeight: "17.5rem", // 280
  /** Organization avatars: 36 in an option, 30 in the combobox trigger. */
  avatar: "2.25rem", // 36
  avatarCompact: "1.875rem", // 30
  /** Role badge: 20 in an option, 22 in the combobox trigger. */
  roleBadgeHeight: "1.25rem", // 20
  roleBadgeHeightLarge: "1.375rem", // 22
  /** Narrowest the identity row's email column gets before sign out wraps under it. */
  identityTextMin: "8rem", // 128
  markWidth: "2.75rem", // 44
  iconTile: "3.5rem", // 56
  iconCircle: "4rem", // 64
  /** B1 sparkle on the icon circle. */
  sparkle: "1.25rem", // 20
  /** B2 countdown ring and its hole (a 3px ring). */
  cooldownRing: "1.125rem", // 18
  cooldownRingHole: "0.75rem", // 12
  logoHeight: "2.125rem", // 34 — carousel column
  mobileLogoHeight: "1.75rem", // 28
  /** Column flex-basis values from the spec layout. */
  visualColumnBasis: "47.5rem", // 760
  formColumnBasis: "30rem", // 480
} as const;

/** Spec breakpoints, in rem so they follow the user's root font size. */
export const authMedia = {
  /** Under 1100px the carousel column is hidden and the mobile logo shows. */
  stacked: "(max-width: 68.75rem)",
  /** Under 520px the card tightens its padding and radius. */
  compact: "(max-width: 32.5rem)",
  /**
   * Under 384px the 3-up social row can't fit "Microsoft" with its mark, so
   * it stacks. Not in the spec, whose phone capture is 390px wide.
   */
  socialStack: "(max-width: 24rem)",
  reducedMotion: "(prefers-reduced-motion: reduce)",
} as const;

/**
 * Stacking inside the card. The open organization menu (spec `.mb-combo`
 * z-index 5) overlays the Continue button and identity row below it.
 */
export const authLayers = {
  combobox: 5,
} as const;

// ── Motion ──────────────────────────────────────────────────────────────────

export const authMotion = {
  /** Spec easing — no bounce. */
  easeOut: "cubic-bezier(0.22, 1, 0.36, 1)",
  press: "120ms",
  control: "200ms",
  slideOut: "500ms",
  slideIn: "600ms",
} as const;

// ── Typography ──────────────────────────────────────────────────────────────

export interface AuthTypeRole {
  fontSize: string;
  lineHeight: string;
  fontWeight: number;
  letterSpacing?: string;
  textTransform?: "uppercase";
}

/**
 * Spec type roles. The app scale has no 11/15/28/44px steps, so roles that
 * match it (legal = Caption 4) point at the shared tokens and the rest are
 * declared here.
 */
export const authTypeRoles = {
  carouselHeadline: {
    fontSize: "2.75rem",
    lineHeight: "3rem",
    fontWeight: fontWeights.medium,
    letterSpacing: "-0.03em",
  },
  kicker: {
    fontSize: "0.6875rem",
    lineHeight: "0.875rem",
    fontWeight: fontWeights.bold,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
  },
  title: {
    fontSize: "1.75rem",
    lineHeight: "2.25rem",
    fontWeight: fontWeights.semibold,
    letterSpacing: "-0.02em",
  },
  subtitle: { fontSize: "0.9375rem", lineHeight: "1.5rem", fontWeight: fontWeights.regular },
  body: { fontSize: "0.875rem", lineHeight: "1.375rem", fontWeight: fontWeights.regular },
  input: { fontSize: "0.9375rem", lineHeight: "1.5rem", fontWeight: fontWeights.regular },
  emphasis: { fontSize: "0.9375rem", lineHeight: "1.5rem", fontWeight: fontWeights.semibold },
  label: { fontSize: "0.8125rem", lineHeight: "1.125rem", fontWeight: fontWeights.semibold },
  button: { fontSize: "0.9375rem", lineHeight: "1.25rem", fontWeight: fontWeights.semibold },
  buttonSmall: { fontSize: "0.8125rem", lineHeight: "1.25rem", fontWeight: fontWeights.semibold },
  small: { fontSize: "0.8125rem", lineHeight: "1.25rem", fontWeight: fontWeights.regular },
  /**
   * Card footer lines (prompt and legal). The README lists legal at 12/18
   * ink-500, but the prototype's `.mb-cardfoot p` rule wins, so every
   * reference screen shows 14px; this matches the references.
   */
  footnote: { fontSize: "0.875rem", lineHeight: "1.125rem", fontWeight: fontWeights.regular },
  /**
   * Helper above "Resend email" (B1/B2). `.mb-small` says 13/20, but the
   * prototype's `.mb-cardfoot p` rule wins on size, so the references show 14/20.
   */
  hint: { fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: fontWeights.regular },
  /** "or continue with" — the prototype's line-height is `normal` (~15px). */
  divider: { fontSize: "0.75rem", lineHeight: "1rem", fontWeight: fontWeights.medium },
  /** Success banner ("We sent you a new link…"). */
  smallStrong: { fontSize: "0.8125rem", lineHeight: "1.25rem", fontWeight: fontWeights.semibold },
  error: { fontSize: "0.8125rem", lineHeight: "1.125rem", fontWeight: fontWeights.medium },
  /**
   * Page footer on the dark ground (copyright, then `footer` for its links).
   * The prototype's `.mb-auth__foot` leaves line-height at `normal`, which is
   * 15px for 12px Plus Jakarta Sans; 18 pushed the centred card 2px up.
   */
  legal: {
    fontSize: fontSizes["caption-4"],
    lineHeight: "0.9375rem",
    fontWeight: fontWeights.regular,
  },
  footer: {
    fontSize: fontSizes["caption-3"],
    lineHeight: "0.9375rem",
    fontWeight: fontWeights.medium,
  },
  badge: { fontSize: "0.6875rem", lineHeight: "1.25rem", fontWeight: fontWeights.bold },
  /** Organization name in an option and the signed-in email (C1–C3): 14/20 600. */
  itemTitle: { fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: fontWeights.semibold },
  /** "Signed in as" above the email (`.mb-identity__text span`): 12/16. */
  itemCaption: { fontSize: "0.75rem", lineHeight: "1rem", fontWeight: fontWeights.regular },
} as const satisfies Record<string, AuthTypeRole>;

export type AuthTypeRoleName = keyof typeof authTypeRoles;

/**
 * Same CSS shape as `typographyMixin`, for any auth type role. Exported so
 * other auth role sets (the carousel's) render through the one helper.
 */
export const authTypeCss = (style: AuthTypeRole): string => `
    font-family: ${fontFamilies.sans};
    font-size: ${style.fontSize};
    font-weight: ${style.fontWeight};
    line-height: ${style.lineHeight};
    letter-spacing: ${style.letterSpacing ?? "0"};
    ${style.textTransform ? `text-transform: ${style.textTransform};` : ""}
  `;

/** Same CSS shape as `typographyMixin`, for the auth-only type roles. */
export const authTypography = (role: AuthTypeRoleName): string =>
  authTypeCss(authTypeRoles[role]);
